<template>
  <div class="profile-container">
    <div class="profile-card">
      <div class="profile-header">
        <img :src="authorData.profile_photo || defaultProfile" alt="Foto de perfil" class="profile-image" />
        <div class="profile-info">
          <div class="name-container">
            <h1 class="name">{{ authorData.name || 'Nome não disponível' }}</h1>
            <div v-if="isUserAdmin" class="admin-mode-switch">
              <v-switch
                v-model="adminModeActive"
                label="Ativar Modo Administrador"
                color="red"
                inset
                hide-details
                dense
              ></v-switch>
            </div>
          </div>
          <p class="nickname">@{{ authorData.nickname || 'nickname' }}</p>
          <p class="bio">{{ authorData.description || 'Sem descrição.' }}</p>

          <div class="extra-info">
            <span v-if="authorData.country">
              <v-icon color="red" size="20">mdi-map-marker</v-icon>
              {{ authorData.country }} {{ authorData.estate }}
            </span>
            <span v-if="authorData.birthday">
              <v-icon color="#fff" size="20">mdi-cake</v-icon>
              {{ formatDate(authorData.birthday) }}
            </span>
            <span v-if="authorData.gender">
              <v-icon color="#fff" size="20">mdi-gender-male-female</v-icon>
              {{ authorData.gender === 'M' ? 'Masculino' : 'Feminino' }}
            </span>
          </div>

          <div class="socials">
            <v-btn icon v-if="authorData.link_facebook" :href="authorData.link_facebook" target="_blank">
              <v-icon color="#3b5998">mdi-facebook</v-icon>
            </v-btn>
            <v-btn icon v-if="authorData.link_instagram" :href="authorData.link_instagram" target="_blank">
              <v-icon color="#E4405F">mdi-instagram</v-icon>
            </v-btn>
            <v-btn icon v-if="authorData.link_twitter" :href="authorData.link_twitter" target="_blank">
              <v-icon color="#1DA1F2">mdi-twitter</v-icon>
            </v-btn>
          </div>

          <div class="stats">
            <div class="stat-box">
              <strong>{{ publishedWorksCount }}</strong>
              <span>Obras publicadas</span>
            </div>
            <div class="stat-box">
              <strong>{{ favoriteProjectsCount }}</strong>
              <span>Favoritas</span>
            </div>
            <div class="stat-box">
              <strong>{{ followersCount }}</strong>
              <span>Seguidores</span>
            </div>
            <div class="stat-box">
              <strong>{{ followingCount }}</strong>
              <span>Seguindo</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isUserAdmin, getAdminMode, setAdminMode } from '@/utils/adminMode';

export default {
  name: "UserProfile",
  props: {
    authorData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultProfile: "https://cdn-icons-png.flaticon.com/512/847/847969.png",
      adminModeActive: false,
    };
  },
  computed: {
    isUserAdmin() {
      return isUserAdmin();
    },
    publishedWorksCount() {
      return Array.isArray(this.authorData?.projects) ? this.authorData.projects.length : 0;
    },
    favoriteProjectsCount() {
      return Array.isArray(this.authorData?.follow_projects) ? this.authorData.follow_projects.length : 0;
    },
    followersCount() {
      return this.pickCount('follow_count', 'follows_count', 'followers');
    },
    followingCount() {
      return this.pickCount('follower_count', 'following');
    },
  },
  watch: {
    adminModeActive(newValue) {
      setAdminMode(newValue);
      // Dispara evento customizado para notificar outros componentes
      window.dispatchEvent(new CustomEvent('adminModeChanged', { detail: newValue }));
    },
  },
  mounted() {
    // Recupera o valor do localStorage ao montar o componente
    this.adminModeActive = getAdminMode();
  },
  methods: {
    pickCount(...keys) {
      for (const key of keys) {
        const value = this.authorData?.[key];

        if (value !== undefined && value !== null && value !== '') {
          return Number(value) || 0;
        }
      }

      return 0;
    },
    formatDate(date) {
      return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'UTC',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date(date));
    }

  },
};
</script>

<style scoped>
span,
p {
  color: #e6e4e7 !important;
}

.profile-container {
  padding: 16px;
  width: 100% !important;
  margin: auto;
}

.profile-card {
  background-color: #000;
  border-radius: 20px;
  width: 100% !important;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #2a2a2a;
}

.profile-header {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.profile-image {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #a21bf0;
}

.profile-info {
  flex: 1;
  min-width: 240px;
}

.name-container {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.name {
  font-size: 26px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

.admin-mode-switch {
  display: flex;
  align-items: center;
}

.admin-mode-switch ::v-deep .v-input--switch {
  margin: 0;
  padding: 0;
}

.admin-mode-switch ::v-deep .v-input--switch .v-label {
  font-size: 14px;
  color: #ccc;
}

.admin-mode-switch ::v-deep .v-input--switch.v-input--is-label-active .v-label {
  color: #f44336;
  font-weight: 600;
}

/* Estilo para switch desativado - borda cinza */
.admin-mode-switch ::v-deep .v-input--switch:not(.v-input--is-label-active) .v-input--switch__track {
  border: 2px solid #666;
  background-color: transparent !important;
}

.admin-mode-switch ::v-deep .v-input--switch:not(.v-input--is-label-active) .v-input--switch__thumb {
  color: #999;
}

/* Estilo para switch ativado */
.admin-mode-switch ::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__track {
  background-color: #f44336 !important;
  border: 2px solid #f44336;
}

.nickname {
  color: #bbb;
  font-size: 16px;
}

.bio {
  margin-top: 10px;
  font-size: 16px;
  color: #ddd;
}

.extra-info {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
}

.socials {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.stat-box {
  text-align: center;
  background-color: #111;
  border-radius: 12px;
  padding: 10px;
  border: 1px solid #2a2a2a;
}

.stat-box strong {
  display: block;
  font-size: 18px;
  color: #a21bf0;
}

.stat-box span {
  font-size: 14px;
  color: #ccc;
}

@media (max-width: 600px) {
  .profile-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .name-container {
    flex-direction: column;
    gap: 8px;
  }

  .admin-mode-switch ::v-deep .v-input--switch .v-label {
    font-size: 12px;
  }

  .extra-info {
    align-items: center;
  }

  .socials {
    justify-content: center;
  }
}

@media (min-width: 1800px) {
  .profile-card {
    padding: 40px;
  }

  .name {
    font-size: 32px;
  }

  .bio {
    font-size: 18px;
  }

  .stat-box strong {
    font-size: 22px;
  }

  .stat-box span {
    font-size: 16px;
  }
}
</style>
